import { state } from "../state";
import { createPlayer } from "../core/player";
import i18next from "i18next";

export function resetControls() {
    const animSelect = document.getElementById("animation-select") as HTMLSelectElement;
    const skinSelect = document.getElementById("skin-select") as HTMLSelectElement;
    const speedRange = document.getElementById("speed-range") as HTMLInputElement;
    const minSpeedInput = document.getElementById("min-speed-input") as HTMLInputElement;
    const maxSpeedInput = document.getElementById("max-speed-input") as HTMLInputElement;

    const updateSpeedRange = () => {
        if (!speedRange || !minSpeedInput || !maxSpeedInput) return;

        let min = parseFloat(minSpeedInput.value);
        let max = parseFloat(maxSpeedInput.value);

        if (isNaN(min)) min = 0.1;
        if (isNaN(max)) max = 2.0;

        if (min <= 0) min = 0.1;
        if (max <= min) max = min + 0.1;

        speedRange.min = min.toString();
        speedRange.max = max.toString();

        let current = parseFloat(speedRange.value);
        let changed = false;

        if (current < min) {
            current = min;
            changed = true;
        } else if (current > max) {
            current = max;
            changed = true;
        }

        if (changed) {
            speedRange.value = current.toString();
            updateRangeValue(speedRange, "speed-value", "x");
            state.currentIframe?.contentWindow?.postMessage({ 
                type: 'set_speed', 
                payload: { speed: current } 
            }, '*');
        }
    };

    minSpeedInput?.addEventListener('change', updateSpeedRange);
    maxSpeedInput?.addEventListener('change', updateSpeedRange);

    const scaleRange = document.getElementById("scale-range") as HTMLInputElement;
    
    if (animSelect) animSelect.innerHTML = `<option value="">${i18next.t("default")}</option>`;
    if (skinSelect) skinSelect.innerHTML = `<option value="">${i18next.t("default")}</option>`;
    if (speedRange) { speedRange.value = "1.0"; updateRangeValue(speedRange, "speed-value", "x"); }
    if (scaleRange) { scaleRange.value = "1.0"; updateRangeValue(scaleRange, "scale-value", "x"); }
}

export function updateRangeValue(input: HTMLInputElement, valueId: string, suffix: string = "") {
    const valEl = document.getElementById(valueId);
    if (valEl) valEl.textContent = input.value + suffix;
}

export function setActiveListItem(id: string) {
  const list = document.getElementById("spine-list");
  if (!list) return;
  const items = list.querySelectorAll<HTMLLIElement>(".spine-item");
  items.forEach((item) => {
    if (item.dataset.id === id) {
      item.classList.add("active");
    } else {
      item.classList.remove("active");
    }
  });
}

export function ensureSpaceKeyListener() {
  if (state.spaceKeyListenerAttached) return;
  window.addEventListener("keydown", (event) => {
    if (event.code !== "Space" && event.key !== " ") return;
    const target = event.target as HTMLElement | null;
    if (target) {
      const tagName = target.tagName;
      if (
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        (target as HTMLElement).isContentEditable
      ) {
        return;
      }
    }
    event.preventDefault();
    if (state.currentPlayerProxy) {
        state.currentIframe?.contentWindow?.postMessage({ type: 'toggle_playback' }, '*');
    }
  });
  state.spaceKeyListenerAttached = true;
}

export function setupPanelControls() {
    const tabs = document.querySelectorAll('.panel-tab');
    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            tabs.forEach(t => t.classList.remove('active'));
            tab.classList.add('active');
            
            const targetId = `tab-${(tab as HTMLElement).dataset.tab}`;
            document.querySelectorAll('.panel-section').forEach(sec => {
                sec.classList.remove('active');
                if (sec.id === targetId) sec.classList.add('active');
            });
        });
    });

    const pmaCheckbox = document.getElementById("pma-checkbox") as HTMLInputElement;
    if (pmaCheckbox) {
        pmaCheckbox.checked = state.pmaEnabled;
        pmaCheckbox.addEventListener('change', () => {
            state.pmaEnabled = pmaCheckbox.checked;
            if (state.currentIframe) {
                state.currentIframe.contentWindow?.postMessage({
                    type: 'update_config',
                    payload: { premultipliedAlpha: state.pmaEnabled }
                }, '*');
            } else if (state.currentActiveId) {
                 const entry = state.spineEntries.find(e => e.id === state.currentActiveId);
                 if (entry) void createPlayer(entry);
            }
        });
    }

    const debugBonesCheckbox = document.getElementById("debug-bones-checkbox") as HTMLInputElement;
    if (debugBonesCheckbox) {
        debugBonesCheckbox.addEventListener('change', () => {
            state.currentIframe?.contentWindow?.postMessage({
                type: 'set_debug',
                payload: { bones: debugBonesCheckbox.checked }
            }, '*');
        });
    }

    const debugBoundsCheckbox = document.getElementById("debug-bounds-checkbox") as HTMLInputElement;
    if (debugBoundsCheckbox) {
        debugBoundsCheckbox.addEventListener('change', () => {
            state.currentIframe?.contentWindow?.postMessage({
                type: 'set_debug',
                payload: { bounds: debugBoundsCheckbox.checked }
            }, '*');
        });
    }

    const animList = document.getElementById("animation-list");
    animList?.addEventListener('click', (e) => {
        const target = (e.target as HTMLElement).closest('.list-chip') as HTMLElement;
        if (target && animList) {
             const current = animList.querySelector('.active');
             if (current) current.classList.remove('active');
             target.classList.add('active');

             const val = target.dataset.value;
             state.currentIframe?.contentWindow?.postMessage({ 
                 type: 'set_animation', 
                 payload: { name: val } 
             }, '*');
             state.currentIframe?.contentWindow?.postMessage({ type: 'play' }, '*');
        }
    });

    const skinList = document.getElementById("skin-list");
    skinList?.addEventListener('click', (e) => {
        const target = (e.target as HTMLElement).closest('.list-chip') as HTMLElement;
        if (target && skinList) {
             const current = skinList.querySelector('.active');
             if (current) current.classList.remove('active');
             target.classList.add('active');

             const val = target.dataset.value;
             state.currentIframe?.contentWindow?.postMessage({ 
                 type: 'set_skin', 
                 payload: { name: val } 
             }, '*');
             state.currentIframe?.contentWindow?.postMessage({ type: 'play' }, '*');
        }
    });

    const speedRange = document.getElementById("speed-range") as HTMLInputElement;
    speedRange?.addEventListener('input', () => {
        updateRangeValue(speedRange, "speed-value", "x");
        state.currentIframe?.contentWindow?.postMessage({ 
            type: 'set_speed', 
            payload: { speed: parseFloat(speedRange.value) } 
        }, '*');
    });

    const scaleRange = document.getElementById("scale-range") as HTMLInputElement;
    scaleRange?.addEventListener('input', () => {
        updateRangeValue(scaleRange, "scale-value", "x");
        state.currentIframe?.contentWindow?.postMessage({ 
            type: 'set_scale', 
            payload: { scale: parseFloat(scaleRange.value) } 
        }, '*');
    });

    const minScaleInput = document.getElementById("min-scale-input") as HTMLInputElement;
    const maxScaleInput = document.getElementById("max-scale-input") as HTMLInputElement;
    
    const updateScaleRange = () => {
        if (!scaleRange || !minScaleInput || !maxScaleInput) return;
        
        let min = parseFloat(minScaleInput.value);
        let max = parseFloat(maxScaleInput.value);
        
        if (isNaN(min)) min = 0.1;
        if (isNaN(max)) max = 10.0;
        
        if (min <= 0) min = 0.01;
        if (max <= min) max = min + 0.1;
        
        scaleRange.min = min.toString();
        scaleRange.max = max.toString();
        
        let current = parseFloat(scaleRange.value);
        let changed = false;
        
        if (current < min) {
            current = min;
            changed = true;
        } else if (current > max) {
            current = max;
            changed = true;
        }
        
        if (changed) {
            scaleRange.value = current.toString();
            updateRangeValue(scaleRange, "scale-value", "x");
            state.currentIframe?.contentWindow?.postMessage({ 
                type: 'set_scale', 
                payload: { scale: current } 
            }, '*');
        }
    };

    minScaleInput?.addEventListener('change', updateScaleRange);
    maxScaleInput?.addEventListener('change', updateScaleRange);

    const resetBtn = document.getElementById("reset-view-btn");
    resetBtn?.addEventListener('click', () => {
        if (scaleRange) {
            scaleRange.value = "1.0";
            updateRangeValue(scaleRange, "scale-value", "x");
        }
        state.currentIframe?.contentWindow?.postMessage({ type: 'reset_view' }, '*');
    });
}
